/**
 * UsersPage Component
 *
 * Admin page for managing platform users.
 */

import React, { useState } from 'react';
import { AdminLayout } from '../layout';
import { useUsers, useUserActions, UsersFilter, User, UserRole } from './hooks';
import { UserFilters, UserRow, ChangeRoleModal, SuspendUserModal } from './components';

export const UsersPage: React.FC = () => {
  const [filter, setFilter] = useState<UsersFilter>({});
  const [page, setPage] = useState(0);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showSuspendModal, setShowSuspendModal] = useState(false);

  const { users, totalCount, isLoading, hasMore, totalPages } = useUsers(filter, page);
  const { changeRole, suspendUser, unsuspendUser, isChangingRole, isSuspending } =
    useUserActions();

  const handleSelectAll = (checked: boolean) => {
    setSelectedUsers(checked ? users.map((u) => u.id) : []);
  };

  const handleSelectUser = (userId: string, selected: boolean) => {
    setSelectedUsers((prev) =>
      selected ? [...prev, userId] : prev.filter((id) => id !== userId)
    );
  };

  const handleUserAction = (
    user: User,
    action: 'view' | 'role' | 'suspend' | 'unsuspend'
  ) => {
    setSelectedUser(user);
    if (action === 'role') {
      setShowRoleModal(true);
    } else if (action === 'suspend') {
      setShowSuspendModal(true);
    } else if (action === 'unsuspend') {
      unsuspendUser(user.id);
    }
  };

  const handleRoleChange = async (role: UserRole) => {
    if (!selectedUser) return false;
    return changeRole({ userId: selectedUser.id, role });
  };

  const handleSuspend = async (reason: string, duration: 'temporary' | 'permanent') => {
    if (!selectedUser) return false;
    return suspendUser({ userId: selectedUser.id, reason, duration });
  };

  const exportToCSV = () => {
    // Create CSV content
    const headers = ['ID', 'Name', 'Email', 'Role', 'Status', 'Registered', 'Legacy', 'Retrieved', 'Retrieved At'];
    const rows = users.map((u) => [
      u.id,
      u.displayName,
      u.email,
      u.role,
      u.isSuspended ? 'Suspended' : 'Active',
      u.createdAt.toISOString().split('T')[0],
      u.wpUserId ? 'Yes' : 'No',
      u.wpUserId ? (u.accountRetrieved ? 'Yes' : 'No') : '',
      u.accountRetrievedAt ? u.accountRetrievedAt.toISOString().split('T')[0] : '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((r) => r.map((c) => `"${c}"`).join(',')),
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `users-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage platform users and their accounts
            </p>
          </div>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <DownloadIcon className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <UserFilters filter={filter} onChange={setFilter} totalCount={totalCount} />

        {/* User Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
              <p className="text-gray-500 mt-2">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No users found</p>
              {(filter.search || filter.role || filter.status || filter.migration) && (
                <button
                  onClick={() => setFilter({})}
                  className="mt-2 text-primary-500 hover:text-primary-600"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="p-4 text-left">
                        <input
                          type="checkbox"
                          checked={
                            selectedUsers.length > 0 &&
                            selectedUsers.length === users.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
                        />
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        User
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Role
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Status
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600 hidden md:table-cell">
                        Registered
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600 hidden lg:table-cell">
                        Last Login
                      </th>
                      <th className="p-4 text-left text-sm font-medium text-gray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <UserRow
                        key={user.id}
                        user={user}
                        isSelected={selectedUsers.includes(user.id)}
                        onSelect={(selected) => handleSelectUser(user.id, selected)}
                        onAction={(action) => handleUserAction(user, action)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Page {page + 1} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasMore}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedUsers.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-xl shadow-2xl px-6 py-4 flex items-center gap-6 z-40">
            <span className="text-sm">
              <span className="font-medium">{selectedUsers.length}</span> user
              {selectedUsers.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  // For bulk operations, use first selected user
                  const user = users.find((u) => u.id === selectedUsers[0]);
                  if (user) {
                    setSelectedUser(user);
                    setShowRoleModal(true);
                  }
                }}
                className="px-3 py-1.5 bg-white text-gray-900 text-sm rounded-lg hover:bg-gray-100 transition-colors"
              >
                Change Role
              </button>
              <button
                onClick={() => {
                  const user = users.find((u) => u.id === selectedUsers[0]);
                  if (user) {
                    setSelectedUser(user);
                    setShowSuspendModal(true);
                  }
                }}
                className="px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
              >
                Suspend
              </button>
            </div>
            <button
              onClick={() => setSelectedUsers([])}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {selectedUser && (
        <>
          <ChangeRoleModal
            isOpen={showRoleModal}
            onClose={() => {
              setShowRoleModal(false);
              setSelectedUser(null);
            }}
            user={selectedUser}
            onSave={handleRoleChange}
            isSaving={isChangingRole}
          />
          <SuspendUserModal
            isOpen={showSuspendModal}
            onClose={() => {
              setShowSuspendModal(false);
              setSelectedUser(null);
            }}
            user={selectedUser}
            onSuspend={handleSuspend}
            isSuspending={isSuspending}
          />
        </>
      )}
    </AdminLayout>
  );
};

// Icons
const DownloadIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const UsersIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const XIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default UsersPage;
